/**
 * Imported environments in a CLOUD (REST) workspace — saved to the server, in
 * ONE call per import.
 *
 * ## The bug this closes (28.09.2026)
 *
 * `handleImportEnvironment` only put the new environment into the in-memory
 * variables store. A local workspace persists that store through its snapshot
 * writer; a cloud workspace persists variable scopes only through
 * `PUT /workspaces/:id/variables`, which the import never called. So a Postman,
 * Hoppscotch or Bruno environment imported into a cloud workspace was there
 * until the next reload (or workspace switch), then gone — for the importer and
 * for every team member, who never saw it at all.
 *
 * ## One call, however many environments
 *
 * Callers add environments one at a time in a synchronous loop (a Hoppscotch
 * "export all", or a Bruno collection with its environments). Each add asks for
 * a save; the saves are coalesced onto one microtask, which sends the store's
 * FULL environment list once — the same body the Environments modal sends.
 */
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore, setWorkspaces, setCurrentWorkspace } from '../store/workspaceStore';
import {
  useVariablesStore,
  setEnvironments,
  setGlobalVariables,
  setWorkspaceVariables,
} from '../store/variablesStore';
import { workspaceService } from './workspaceService';
import { isCloudRestWorkspace } from '../utils/workspaceStorage';
import { restoreVarKeyCase } from '../utils/variableScopes';

let pending = null;

/**
 * Ask for the current workspace's environments to be saved to the server.
 * Calls made in the same tick share one PUT. Resolves to the server response,
 * or null when there was nothing to do (not a cloud-REST workspace).
 *
 * @param {{ onError?: (err: Error) => void }} [opts]
 */
export function saveImportedEnvironmentsToCloud({ onError } = {}) {
  if (pending) return pending;
  pending = Promise.resolve().then(async () => {
    pending = null;
    try {
      return await flush();
    } catch (err) {
      onError?.(err);
      return null;
    }
  });
  return pending;
}

async function flush() {
  const workspace = useWorkspaceStore.getState().currentWorkspace;
  const isAuthenticated = useAuthStore.getState().isAuthenticated;
  if (!isCloudRestWorkspace(workspace, isAuthenticated)) return null;

  const { globalVariables, workspaceVariables, environments } = useVariablesStore.getState();
  const response = await workspaceService.updateVariableScopes(workspace.id, {
    globals: globalVariables,
    workspace_globals: workspaceVariables,
    environments,
  });

  // Same bookkeeping as the Environments modal's save (useVariablesScopes): the
  // workspace object carries `variable_scopes`, and a later switch back to this
  // workspace hydrates from it — without this, the environment would vanish on
  // the next switch even though the server has it.
  const variableScopes = response?.variable_scopes || {};
  const settings = response?.settings || {};
  setGlobalVariables(restoreVarKeyCase(globalVariables, variableScopes.globals) || globalVariables);
  setWorkspaceVariables(
    restoreVarKeyCase(workspaceVariables, variableScopes.workspace_globals) || workspaceVariables
  );
  setEnvironments(variableScopes.environments || environments);
  setWorkspaces((prev) =>
    prev.map((ws) => (ws.id === workspace.id ? { ...ws, settings, variable_scopes: variableScopes } : ws))
  );
  setCurrentWorkspace((prev) =>
    prev && prev.id === workspace.id ? { ...prev, settings, variable_scopes: variableScopes } : prev
  );
  return response;
}
